/**
 * RichHelpText — the single shared safe-render component for help text
 * (application-builder-rich-help-text spec, task 7.2).
 *
 * One component renders a Help_Text_Surface's help for BOTH the applicant
 * surface and the builder preview (Req 4.1, 4.4), so what a Director previews is
 * byte-for-byte what an applicant sees. It has two modes, keyed off the stored
 * `format` indicator:
 *
 *   - HTML (Req 4.1, 4.3): when `format === 'HTML'` and `helpTextHtml` is
 *     non-empty, the stored (already server-sanitized) HTML is re-sanitized on
 *     the client through the shared `HELP_TEXT_PURIFY_CONFIG` allow-list and
 *     rendered via `dangerouslySetInnerHTML`. The client re-sanitize is
 *     defense-in-depth — even a corrupted or legacy stored value can never
 *     inject active markup (property P8.1).
 *   - PLAIN (Req 4.2, 5.1, 5.2): for any other case — `format` missing, `PLAIN`,
 *     or HTML mode with no HTML content — the plain `helpText` is rendered as
 *     text inside a `<p>`. This path NEVER uses `dangerouslySetInnerHTML`, so a
 *     plain string containing angle brackets is shown literally, never
 *     interpreted as markup.
 *
 * Empty content renders nothing (returns `null`), matching the existing
 * truthiness guards at the call sites.
 *
 * Mirrors the DOMPurify render precedent in
 * `frontend/src/components/messages/stream/slack/renderMarkdown.ts`.
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 5.1, 5.2
 */
import * as React from 'react';
import DOMPurify from 'dompurify';

import { HELP_TEXT_PURIFY_CONFIG } from '@/lib/application-form/richHelpText';

export interface RichHelpTextProps {
  /** The surface's format indicator; missing/`PLAIN` ⇒ render as plain text (Req 4.2, 5.1). */
  format?: 'PLAIN' | 'HTML' | null;
  /** Plain help text (used when `format` is `PLAIN`/absent). */
  helpText?: string | null;
  /** Sanitized allow-listed HTML (used when `format` is `HTML`). */
  helpTextHtml?: string | null;
  /** Optional class applied to the rendered container. */
  className?: string;
  /** Optional DOM id (e.g. for `aria-describedby` / `aria-controls` wiring). */
  id?: string;
}

/**
 * Render a Help_Text_Surface's help safely. HTML mode re-sanitizes and injects
 * the allow-listed HTML; every other case renders plain text.
 */
export function RichHelpText({
  format,
  helpText,
  helpTextHtml,
  className,
  id,
}: RichHelpTextProps): React.ReactElement | null {
  if (format === 'HTML' && helpTextHtml) {
    // Defense-in-depth re-sanitize (Req 4.3, P8.1). The server pass is
    // authoritative; this guarantees only allow-listed formatting reaches the
    // DOM regardless of what was stored.
    const clean = String(DOMPurify.sanitize(helpTextHtml, HELP_TEXT_PURIFY_CONFIG));
    // If sanitization strips everything to whitespace, render nothing.
    if (!clean.trim()) {
      return null;
    }
    return (
      <div
        id={id}
        className={className}
        // eslint-disable-next-line react/no-danger -- content is twice-sanitized against the fixed allow-list.
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }

  // PLAIN / missing-indicator path (Req 4.2, 5.1, 5.2): render as text, never as
  // markup. No `dangerouslySetInnerHTML` here.
  if (!helpText) {
    return null;
  }
  return (
    <p id={id} className={className}>
      {helpText}
    </p>
  );
}

export default RichHelpText;
